import { Col, Row } from 'antd';
import moment from 'moment';
import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SessionDatePicker from '../containers/session-date-picker/session-date-picker';
import GamePicker from '../components/game-picker/game-picker';
import ZLSection from '../containers/zl-section/zl-section';
import { actionCreators as SessionActions } from '../store/Sessions';
import { actionCreators as BookingActions, BookingStatus } from '../store/Booking';
import { useSelector, useDispatch } from 'react-redux';
import { IApplicationState } from '../store';
import { PurchaseTypes } from '../store/PurchaseTypes';

// number of month tabs shown above the calendar at once
const VISIBLE_MONTHS = 3;

// furthest month (from today) the calendar can be paged to
const MAX_MONTHS_AHEAD = 12;

export interface IDatePickerSectionProps {
    active: boolean;
    scrollTo?: boolean;
}

const DatePickerSection: React.FunctionComponent<IDatePickerSectionProps> = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { active, scrollTo } = props;

    const selectedLanguage = useSelector((state: IApplicationState) => state.intl.selectedLanguage);
    const loading = useSelector((state: IApplicationState) => state.session.openDatesLoading);
    const availableDates = useSelector((state: IApplicationState) => state.session.openDates);
    const loadedThrough = useSelector((state: IApplicationState) => state.session.openDatesLoadedThrough);
    const extending = useSelector((state: IApplicationState) => state.session.openDatesExtending);
    const exhausted = useSelector((state: IApplicationState) => state.session.openDatesExhausted);
    const pendingBooking = useSelector((state: IApplicationState) => state.booking.booking);
    const bookingStatus = useSelector((state: IApplicationState) => state.booking.status);
    const selectedDate = useSelector((state: IApplicationState) => state.session.selectedDate);
    const selectedGame = useSelector((state: IApplicationState) => state.games.selectedGame);
    const selectedPurchaseType = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseType);

    // Map.keys() returns an iterator — .next().value gets the first (earliest) available date to seed the calendar
    const initalDate = availableDates?.keys().next().value as string | undefined;

    const calendarStartDate = useMemo(() => {
        return moment(initalDate).startOf('day');
    }, [initalDate]);

    // how many months the visible month tabs have been paged forward from the first available month
    const [monthOffset, setMonthOffset] = useState(0);

    // a fresh open dates load starts the tabs back at the first available month
    useEffect(() => {
        setMonthOffset(0);
    }, [initalDate]);

    const windowStart = useMemo(
        () => calendarStartDate.clone().startOf('month').add(monthOffset, 'months'),
        [calendarStartDate, monthOffset]
    );
    const windowEnd = useMemo(
        () => windowStart.clone().add(VISIBLE_MONTHS - 1, 'months').endOf('month'),
        [windowStart]
    );

    const monthButtons = useMemo(() => {
        return Array.from({ length: VISIBLE_MONTHS }, (_, i) => {
            const month = windowStart.clone().add(i, 'months');
            return {
                label: moment().locale(selectedLanguage.momentLocale).month(month.month()).format('MMMM'),
                month: month.month(),
                year: month.year(),
            };
        });
    }, [windowStart, selectedLanguage.momentLocale]);

    const canPageBack = monthOffset > 0;

    const canPageForward = useMemo(() => {
        const nextMonth = windowEnd.clone().add(1, 'months');
        if (nextMonth.isAfter(moment().add(MAX_MONTHS_AHEAD, 'months'), 'month')) return false;

        // once a search comes back empty, only page through months that have already been loaded
        if (exhausted && loadedThrough && nextMonth.isAfter(moment(loadedThrough, 'YYYY-MM-DD'), 'month')) return false;

        return true;
    }, [windowEnd, exhausted, loadedThrough]);

    // when the visible months run past what has been loaded, fetch the next block (3 months at a time)
    useEffect(() => {
        if (!loadedThrough || extending || exhausted) return;

        if (windowEnd.isAfter(moment(loadedThrough, 'YYYY-MM-DD'), 'day')) {
            dispatch(SessionActions.extendOpenDates());
        }
    }, [windowEnd, loadedThrough, extending, exhausted, dispatch]);

    const onPageMonths = useCallback((delta: number) => {
        setMonthOffset((offset) => Math.max(0, offset + delta));
    }, []);

    const onDateChange = useCallback(
        (date: moment.Moment) => {
            if (pendingBooking && bookingStatus === BookingStatus.Pending) {
                dispatch(BookingActions.expireBooking());
                dispatch(SessionActions.clearSelectedDateTime());
            }

            dispatch(SessionActions.postDateSelectedAction(date.toISOString(true)));
            dispatch(SessionActions.loadSessionTimes(date, selectedGame?.experienceId));
        },
        [bookingStatus, pendingBooking, dispatch, selectedGame]
    );

    const gamePicker = selectedPurchaseType === PurchaseTypes.Booking ? <GamePicker loading={false} /> : null;

    return (
        <ZLSection
            title={t(
                selectedPurchaseType === PurchaseTypes.Booking
                    ? 'sessionDatePicker.titles.chooseExperienceAndDate'
                    : 'sessionDatePicker.titles.chooseDate'
            )}
            active={active}
            scrollTo={scrollTo}
        >
            <Row justify="center">
                <Col xs={24}>
                    {gamePicker}
                    <SessionDatePicker
                        initalDate={initalDate}
                        selectedDate={selectedDate}
                        availableDates={availableDates}
                        monthHeaderOptions={monthButtons}
                        canPageBack={canPageBack}
                        canPageForward={canPageForward}
                        onPageMonths={onPageMonths}
                        onDateChange={onDateChange}
                        loading={loading}
                        extending={extending}
                        locale={selectedLanguage.code}
                    />
                </Col>
            </Row>
            
        </ZLSection>
    );
};

export default DatePickerSection;
